import React from 'react';
import { Box, Collapse, IconButton, Tooltip, Typography, alpha } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

function NoteInlineCode({ children }: React.PropsWithChildren<Record<string, unknown>>) {
  return (
    <Box
      component="code"
      sx={(t) => ({
        px: 0.5,
        py: '1px',
        borderRadius: '4px',
        bgcolor: alpha(t.palette.text.primary, 0.08),
        fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
        fontSize: '0.85em',
      })}
    >
      {children}
    </Box>
  );
}

/**
 * The small "i" button that goes in the top-right corner of a chart/table/
 * metric card's header, next to any other header actions (e.g. a download
 * button). Pair with `ExplainerNote` below, sharing the same `open` state --
 * this component is only the trigger, not the content.
 */
export function ExplainerToggle({
  title,
  open,
  onClick,
}: {
  title: string;
  open: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip title={open ? 'Hide explanation' : title}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={title}
        sx={(t) => ({
          p: 0.375,
          color: open ? 'info.main' : alpha(t.palette.info.main, 0.6),
          flexShrink: 0,
          '&:hover': { color: 'info.main', bgcolor: alpha(t.palette.info.main, 0.08) },
        })}
      >
        <InfoOutlined sx={{ fontSize: 17 }} />
      </IconButton>
    </Tooltip>
  );
}

/**
 * A persistent, plain-English explanation for a chart, table, or metric
 * card that isn't self-explanatory. Renders inline, directly inside the
 * same section it explains (not a popup) -- pass the `open` state from a
 * sibling `ExplainerToggle` icon to show/hide it.
 *
 * `children` is a **markdown string**, not JSX -- write `**bold**` and
 * `` `code` `` directly rather than nesting `<strong>`/`<code>` elements,
 * so the source stays plain text instead of a wall of markup. Body copy
 * should always name: what the number/table represents, what time window
 * it's calculated over, and what it's being compared against -- not just
 * restate the label.
 */
export default function ExplainerNote({
  title = 'How this works',
  open,
  children,
}: {
  title?: string;
  open: boolean;
  children: string;
}) {
  return (
    <Collapse in={open}>
      <Box
        sx={(t) => ({
          mt: 1,
          mb: 1.5,
          p: 1.25,
          borderRadius: `${t.ds.radius.sm ?? 6}px`,
          bgcolor: alpha(t.palette.info.main, 0.06),
          border: `1px solid ${alpha(t.palette.info.main, 0.18)}`,
        })}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.main', display: 'block', mb: 0.75 }}>
          {title}
        </Typography>
        <Box
          sx={{
            fontSize: '0.8125rem',
            lineHeight: 1.6,
            color: 'text.secondary',
            '& p': { m: 0, mb: 1 },
            '& p:last-child': { mb: 0 },
            '& ul, & ol': { m: 0, mt: 0.5, mb: 1, pl: 2.5 },
            '& ul:last-child, & ol:last-child': { mb: 0 },
            '& li': { mb: 0.4 },
            '& li:last-child': { mb: 0 },
            '& strong': { color: 'text.primary', fontWeight: 600 },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ code: NoteInlineCode }}>
            {children}
          </ReactMarkdown>
        </Box>
      </Box>
    </Collapse>
  );
}
